import { Component } from 'react';
import RecoveryScreen from './RecoveryScreen';

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'not a valid JavaScript MIME type',
  'Importing a module script failed',
  'Loading chunk',
  'Loading CSS chunk',
];

function isChunkError(error) {
  if (!error?.message) return false;
  return CHUNK_ERROR_PATTERNS.some(p => error.message.includes(p));
}

function savePosition() {
  // Never save auth-callback URLs — the PKCE code is single-use and may
  // already be consumed by the time the page reloads.
  const { pathname, search } = window.location;
  if (pathname.startsWith('/auth/') || search.includes('code=')) return;
  try {
    sessionStorage.setItem('diwanRecoveryPath', pathname + search);
  } catch { /* non-fatal */ }
}

// Dynamic imports to avoid circular-init / TDZ issues — monitoring and
// analytics initialise lazily; a static import from ErrorBoundary (which
// sits at the root of the module graph) creates ordering ambiguity in the
// production bundle that Rollup resolves incorrectly.
function reportCrash(error, ctx) {
  const msg  = error?.message ?? String(error);
  const path = window.location.pathname;

  // Structured log visible in Vercel function logs
  try { console.error(JSON.stringify({ level: 'error', event: 'app_crashed', message: msg, component: ctx, path, ts: Date.now() })); } catch { /* non-fatal */ }

  // PostHog event — lazy so it never blocks rendering
  import('../lib/analytics')
    .then(m => m.trackEvent('app_crashed', { message: msg, component: ctx, path }))
    .catch(() => {});

  // Sentry via monitoring — lazy for the same reason
  import('../lib/monitoring')
    .then(m => m.captureError(error, `ErrorBoundary: ${ctx}`))
    .catch(() => {});
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunk: false, resetKey: props.resetKey, errorMsg: '' };
    this.handleRetry = this.handleRetry.bind(this);
    this.handleBack  = this.handleBack.bind(this);
  }

  // Reset error state whenever the route changes (resetKey = location.pathname)
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.resetKey) {
      return { hasError: false, isChunk: false, resetKey: props.resetKey, errorMsg: '' };
    }
    return null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunk: isChunkError(error), errorMsg: error?.message ?? String(error) };
  }

  componentDidCatch(error, info) {
    const ctx = info?.componentStack?.split('\n')[1]?.trim() ?? 'unknown';

    if (isChunkError(error)) {
      const { pathname, search } = window.location;
      const inAuthFlow = pathname.startsWith('/auth/') || search.includes('code=');
      if (!inAuthFlow) {
        savePosition();
        setTimeout(() => { window.location.reload(); }, 1000);
      }
      return;
    }

    reportCrash(error, ctx);
  }

  handleRetry() { window.location.reload(); }
  handleBack()  { window.history.back(); }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.isChunk) return <RecoveryScreen onRetry={this.handleRetry} />;

    return (
      <div style={{
        width: '100%',
        minHeight: '100svh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        padding: '2rem',
      }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>

          {/* Glyph */}
          <div style={{
            fontSize: '1.6rem',
            color: 'var(--gold)',
            marginBottom: '1.25rem',
            opacity: 0.7,
            animation: 'arabicPulse 2.5s ease-in-out infinite',
          }}>
            ◆
          </div>

          {/* Headline */}
          <div style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontSize: '1.05rem',
            color: 'var(--text)',
            marginBottom: '0.6rem',
            lineHeight: 1.4,
          }}>
            The page lost its footing
          </div>

          {/* Subtext */}
          <div style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: '0.62rem',
            letterSpacing: '0.08em',
            color: 'var(--text3)',
            lineHeight: 1.8,
            marginBottom: '0.85rem',
          }}>
            Even the best manuscripts have errata.<br />
            We've been notified and we're on it.
          </div>

          {/* Error detail — shown temporarily to aid remote diagnosis */}
          {this.state.errorMsg && (
            <div style={{
              fontFamily: 'monospace',
              fontSize: '0.6rem',
              color: 'var(--text3)',
              background: 'var(--bg2)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              padding: '0.5rem 0.65rem',
              marginBottom: '1.25rem',
              textAlign: 'left',
              wordBreak: 'break-all',
              opacity: 0.7,
            }}>
              {this.state.errorMsg}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center' }}>
            <button
              onClick={this.handleBack}
              style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.58rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '0.65rem 1.1rem',
                color: 'var(--text3)',
                cursor: 'pointer',
              }}
            >
              ← Back
            </button>
            <button
              onClick={this.handleRetry}
              style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.58rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                background: 'var(--gold)',
                border: 'none',
                borderRadius: 4,
                padding: '0.65rem 1.1rem',
                color: '#1a1409',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              Try again →
            </button>
          </div>
        </div>
      </div>
    );
  }
}
